package macha.sh.kotlin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import macha.sh.kotlin.SiteColors
import macha.sh.kotlin.SiteTypography
import macha.sh.kotlin.applyFont
import macha.sh.kotlin.components.layouts.PageLayoutData
import macha.sh.kotlin.components.layouts.SiteContentMaxWidth
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text
import kotlinx.browser.window

@InitRoute
fun initContactPage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Contact",
            description = "Direct contact channels for Macha — email, phone, developer forum, and GitHub.",
            path = "/contact",
            indexable = false,
            markdownPath = "/text-content/page-content/contact/contact.md",
        )
    )
}

data class ContactChannel(
    val title: String,
    val value: String,
    val href: String,
    val description: String,
    val actionText: String
)

private val CONTACT_CHANNELS = listOf(
    ContactChannel(
        title = "EMAIL",
        value = "ryan@macha.sh",
        href = "mailto:ryan@macha.sh",
        description = "Primary channel for consulting, software engineering, and inquiries.",
        actionText = "Send Email →"
    ),
    ContactChannel(
        title = "PHONE / SMS",
        value = "+1 (347) 450-9116",
        href = "tel:+13474509116",
        description = "Direct voice and text line for quick discussions and urgent questions.",
        actionText = "Call or Text →"
    ),
    ContactChannel(
        title = "OPENAI FORUM",
        value = "@macha on Developer Forum",
        href = "https://community.openai.com/u/macha/summary",
        description = "Technical forum discussions on prompt engineering, LLM systems, and AI APIs.",
        actionText = "Visit Forum Profile →"
    ),
    ContactChannel(
        title = "GITHUB",
        value = "@MachaLvl99",
        href = "https://github.com/MachaLvl99",
        description = "Active code repositories, forks, gists, and open-source contributions.",
        actionText = "Visit GitHub Profile →"
    )
)

@Page("/contact")
@Layout(".components.layouts.PageLayout")
@Composable
fun ContactPage() {
    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(SiteContentMaxWidth)
                .gap(1.5.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header Section
            Box(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(SiteColors.BoxBkg)
                    .border(width = 2.px, style = LineStyle.Solid, color = SiteColors.Border)
                    .padding(1.5.cssRem)
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().gap(0.75.cssRem).textAlign(TextAlign.Center),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    H1(
                        attrs = Modifier
                            .applyFont(SiteTypography.Subheading, fontSize = 2.cssRem)
                            .color(SiteColors.Hover)
                            .toAttrs()
                    ) {
                        Text("CONTACT & COMMUNICATIONS")
                    }
                    SpanText(
                        "Direct channels for technical discussions, software consulting, and collaboration inquiries.",
                        modifier = Modifier.applyFont(SiteTypography.Body, fontSize = 1.15.cssRem)
                    )
                }
            }

            // Contact Channels Grid
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(320px, 1fr))")
                    property("gap", "1rem")
                    property("width", "100%")
                }
            }) {
                CONTACT_CHANNELS.forEach { channel ->
                    ContactCard(channel)
                }
            }

            // Direct Communication Protocol Note
            Box(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(SiteColors.BoxBkg)
                    .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
                    .padding(1.25.cssRem)
            ) {
                Column(modifier = Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                    SpanText(
                        "COMMUNICATION NOTES",
                        modifier = Modifier.applyFont(SiteTypography.Subheading).color(SiteColors.Hover)
                    )
                    SpanText(
                        "Email is the preferred method for project scopes, engineering proposals, and security disclosures. Responses are typically returned within 24–48 hours.",
                        modifier = Modifier.applyFont(SiteTypography.BodyS).color(SiteColors.SiteText)
                    )
                }
            }
        }
    }
}

@Composable
private fun ContactCard(channel: ContactChannel) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(SiteColors.BoxBkg)
            .border(width = 1.px, style = LineStyle.Solid, color = SiteColors.Border)
            .padding(1.25.cssRem)
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(0.75.cssRem)) {
            H2(
                attrs = Modifier
                    .applyFont(SiteTypography.Subheading, fontSize = 1.15.cssRem)
                    .color(SiteColors.Hover)
                    .toAttrs()
            ) {
                Text(channel.title)
            }

            SpanText(
                channel.value,
                modifier = Modifier
                    .applyFont(SiteTypography.Body, fontSize = 1.1.cssRem)
                    .color(SiteColors.Hover)
            )

            SpanText(
                channel.description,
                modifier = Modifier
                    .applyFont(SiteTypography.BodyS)
                    .color(SiteColors.SiteText)
            )

            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(SiteColors.Border))

            Link(
                path = channel.href,
                text = channel.actionText,
                modifier = Modifier
                    .applyFont(SiteTypography.Subheading)
                    .color(SiteColors.Hover),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
            )
        }
    }
}

@Page("/five-dolla-holla")
@Composable
fun FiveDollaHollaRedirect() {
    LaunchedEffect(Unit) {
        window.location.replace("/contact")
    }
    Box(Modifier.fillMaxWidth().padding(2.cssRem), contentAlignment = Alignment.Center) {
        SpanText("Redirecting to Contact…", modifier = Modifier.applyFont(SiteTypography.Body))
    }
}
